<template>
  <div>
    <ChangePackage
      v-model:show-dialog="isMarkeroDowngradeDialog"
      :company="company"
      @downgrade="fetchPendingDowngrade"
    />

    <SettingsCard
      :title="$t('settings.subscription.title')"
      :subtitle="$t('settings.subscription.subtitle')"
      icon="mdi-account-cog"
      :model-value="isOpen"
      @update:modelValue="setOpen"
    >
      <div class="pa-4">
        <!-- SUBSCRIPTION STATUS -->
        <v-row>
          <v-col
            cols="12"
            sm="6"
            class="px-0 px-md-2"
          >
            <div>
              <span class="font-weight-bold">
                {{ $t('settings.subscription.body-texts.0') }}<br />
              </span>
              <span class="text-grey">
                {{ $t('settings.subscription.body-texts.1', { readablePlatformName }) }}
              </span>
            </div>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            class="px-0 px-md-2"
          >
            <div class="d-flex flex-row mt-4">
              <SubscriptionState :subscription-state="subscriptionState" />

              <Permission permission="settings-subscription:change">
                <Feature :feature-slug="featureNames.CHANGE_SUBSCRIPTION">
                  <v-btn
                    color="primary"
                    variant="text"
                    class="ml-4"
                    :class="{ 'w-full mt-4': $vuetify.display.smAndDown }"
                    :disabled="!!pendingDowngrade?.downgradeAt"
                    @click="handleDowngrade"
                  >
                    <v-icon>mdi-swap-horizontal</v-icon>
                    {{ $t('settings.subscription.options') }}
                  </v-btn>
                </Feature>
              </Permission>
            </div>

            <Permission permission="settings-subscription:cancel">
              <component
                :is="currentStep"
                v-if="subscriptionState"
                :company="company"
                @handleDowngrade="handleDowngrade"
                @actionSuccess="getContractDetails"
              />
              <template v-slot:noPermission>
                <div class="mt-8">
                  <span class="font-weight-bold">
                    {{ $t('settings.subscription.delete-account') }}<br />
                  </span>
                  <span class="text-grey">
                    {{ $t('settings.subscription.delete-account-subtitle') }}
                  </span>
                </div>
              </template>
            </Permission>
          </v-col> </v-row
        ><!-- DELETE ACCOUNT -->
        <Permission permission="settings-subscription:cancel">
          <v-row v-if="showDeleteAccountSection">
            <v-col
              cols="12"
              sm="6"
              class="px-0 px-md-2"
            >
              <div>
                <span class="font-weight-bold">
                  {{ $t('settings.subscription.delete-account-full') }}<br />
                </span>
                <span class="text-grey">
                  {{ $t('settings.subscription.delete-account-full-subtitle') }}
                </span>
              </div>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              class="px-0 px-md-2"
            >
              <DeleteAccount
                :subscription-state="subscriptionState"
                :end-date="endDate"
              />
            </v-col>
          </v-row>
        </Permission>

        <ChangeSubscription
          :dialog="isDowngradeDialog"
          :company="company"
          @close="isDowngradeDialog = false"
        />
      </div>
    </SettingsCard>
  </div>
</template>

<script>
import Permission from '@/components/Permission'
import SettingsCard from '@/components/SettingsCard.vue'
import { useQueryOpen } from '@/composables/useQueryOpen'
import { ProductType } from '@/lib/productTypes'
import billwerkPortalMixin from '@/mixins/billwerkPortal'
import brandingMixing from '@/mixins/branding'
import featureMixin from '@/mixins/feature'
import subscriptionJsMixin from '@/modules/billwerk/lib/subscriptionJsMixin'

import ChangePackage from '../account/ChangePackage.vue'
import GET_PENDING_DOWNGRADE from '../account/queries/GetPendingDowngrade.gql'
import ChangeSubscription from '../ChangeSubscription'
import SubscriptionStateValue from '../enums/SubscriptionState'
import DeleteAccount from './DeleteAccount.vue'
import Active from './steps/Active.vue'
import Cancelled from './steps/Cancelled.vue'
import Free from './steps/Free.vue'
import OnDelete from './steps/OnDelete.vue'
import SubscriptionState from './SubscriptionState.vue'

export default {
  setup() {
    const { isOpen, setOpen } = useQueryOpen({ settingName: 'subscription' })
    return {
      isOpen,
      setOpen,
    }
  },
  components: {
    ChangePackage,
    SettingsCard,
    SubscriptionState,
    DeleteAccount,
    ChangeSubscription,
    Permission,
    Free,
    Active,
    Cancelled,
  },
  mixins: [brandingMixing, subscriptionJsMixin, billwerkPortalMixin, featureMixin],
  props: {
    company: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      revokeCancellationLoading: false,
      contractCancelLoading: false,
      isDowngradeDialog: false,
      isMarkeroDowngradeDialog: false,
    }
  },
  computed: {
    isBeginner() {
      return this.company?.productType === ProductType.BEGINNER
    },
    currentStep() {
      if (this.company?.deleteOn) {
        return OnDelete
      }
      return {
        [SubscriptionStateValue.FREE]: Free,
        [SubscriptionStateValue.ACTIVE]: this.isPlatformMarkero ? Free : Active,
        [SubscriptionStateValue.CANCELED]: Cancelled,
      }[this.subscriptionState]
    },
    showDeleteAccountSection() {
      return [SubscriptionStateValue.CANCELED, SubscriptionStateValue.INACTIVE].includes(
        this.subscriptionState
      )
    },
    endDate() {
      return this.contractDetails?.endDate ? new Date(this.contractDetails.endDate) : null
    },
  },
  methods: {
    handleDowngrade() {
      if (this.isPlatformMarkero) {
        this.isMarkeroDowngradeDialog = true
      } else {
        this.isDowngradeDialog = true
      }
    },
    async fetchPendingDowngrade() {
      this.$apollo.queries.pendingDowngrade.refetch()
    },
  },
  apollo: {
    pendingDowngrade: {
      query: GET_PENDING_DOWNGRADE,
      variables() {
        return {
          companyId: this.$auth.user.companyId,
        }
      },
    },
  },
}
</script>
